import { defineStore } from 'pinia';
import camelcaseKeys from 'camelcase-keys';

import DocumentsAPI from 'dashboard/api/documents';
import DocumentRequestsAPI from 'dashboard/api/documentRequests';
import SignatureRequestsAPI from 'dashboard/api/signatureRequests';
import { throwErrorMessage } from 'dashboard/store/utils/api';

export const useDocumentsStore = defineStore('documents', {
  state: () => ({
    records: [],
    selectedDocument: null,
    documentRequests: [],
    conversationMessages: [],
    summary: {
      total: 0,
      needsAction: 0,
      waiting: 0,
      ready: 0,
      completed: 0,
      overdue: 0,
    },
    meta: { totalCount: 0, currentPage: 1, totalPages: 0 },
    listRequestId: 0,
    transitioningMap: {},
    uploadingMap: {},
    downloadingMap: {},
    uiFlags: {
      fetchingList: false,
      fetchingItem: false,
      fetchingRequests: false,
      listError: false,
      creating: false,
      creatingRequest: false,
      creatingSignatureRequest: false,
      deleting: false,
    },
  }),

  getters: {
    getDocuments: state => camelcaseKeys(state.records, { deep: true }),
    getSelectedDocument: state =>
      state.selectedDocument
        ? camelcaseKeys(state.selectedDocument, { deep: true })
        : null,
    getDocumentRequests: state =>
      camelcaseKeys(state.documentRequests, { deep: true }),
    getSummary: state => camelcaseKeys(state.summary, { deep: true }),
    getMeta: state => camelcaseKeys(state.meta, { deep: true }),
    getUIFlags: state => state.uiFlags,
    isTransitioning: state => id => !!state.transitioningMap[id],
    isUploading: state => id => !!state.uploadingMap[id],
    isDownloading: state => id => !!state.downloadingMap[id],
  },

  actions: {
    setUIFlag(flags) {
      this.uiFlags = { ...this.uiFlags, ...flags };
    },

    setPendingFlag(mapName, id, value) {
      this[mapName] = { ...this[mapName], [id]: value };
    },

    updateDocumentInStore(updated) {
      const index = this.records.findIndex(record => record.id === updated.id);
      if (index !== -1) this.records[index] = updated;
      if (this.selectedDocument?.id === updated.id) {
        this.selectedDocument = updated;
      }
    },

    clearSelectedDocument() {
      this.selectedDocument = null;
      this.documentRequests = [];
    },

    async fetchRequests(documentId) {
      this.setUIFlag({ fetchingRequests: true });
      try {
        const { data } = await DocumentRequestsAPI.get(documentId);
        this.documentRequests = data.payload || [];
        return this.getDocumentRequests;
      } catch (error) {
        this.documentRequests = [];
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingRequests: false });
      }
    },

    async createRequest(payload) {
      this.setUIFlag({ creatingRequest: true });
      try {
        const { data } = await DocumentRequestsAPI.create(payload);
        return camelcaseKeys(data, { deep: true });
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingRequest: false });
      }
    },

    async revokeRequest(id) {
      try {
        const { data } = await DocumentRequestsAPI.revoke(id);
        const index = this.documentRequests.findIndex(
          request => request.id === id
        );
        if (index !== -1) this.documentRequests[index] = data;
        return camelcaseKeys(data, { deep: true });
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    // Signature requests live on the document detail payload; the caller
    // refreshes via show() after these succeed.
    async createSignatureRequest(payload) {
      this.setUIFlag({ creatingSignatureRequest: true });
      try {
        const { data } = await SignatureRequestsAPI.create(payload);
        return camelcaseKeys(data, { deep: true });
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingSignatureRequest: false });
      }
    },

    async voidSignatureRequest(id) {
      try {
        const { data } = await SignatureRequestsAPI.void(id);
        return camelcaseKeys(data, { deep: true });
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async get(params = {}) {
      const requestId = this.listRequestId + 1;
      this.listRequestId = requestId;
      this.setUIFlag({ fetchingList: true, listError: false });
      try {
        const { data } = await DocumentsAPI.get(params);
        if (requestId === this.listRequestId) {
          this.records = data.payload || [];
          this.summary = data.summary || {};
          this.meta = data.meta || {};
        }
        return this.getDocuments;
      } catch (error) {
        if (requestId === this.listRequestId)
          this.setUIFlag({ listError: true });
        return throwErrorMessage(error);
      } finally {
        if (requestId === this.listRequestId)
          this.setUIFlag({ fetchingList: false });
      }
    },

    async show(id) {
      this.setUIFlag({ fetchingItem: true });
      try {
        const { data } = await DocumentsAPI.show(id);
        this.selectedDocument = data;
        return camelcaseKeys(data, { deep: true });
      } catch (error) {
        this.selectedDocument = null;
        throwErrorMessage(error);
        return null;
      } finally {
        this.setUIFlag({ fetchingItem: false });
      }
    },

    async create(formData) {
      this.setUIFlag({ creating: true });
      try {
        const { data } = await DocumentsAPI.create(formData);
        this.records.unshift(data);
        return camelcaseKeys(data, { deep: true });
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creating: false });
      }
    },

    async transition(id, status, note = '') {
      this.setPendingFlag('transitioningMap', id, true);
      try {
        const { data } = await DocumentsAPI.transition(id, status, note);
        this.updateDocumentInStore(data);
        return camelcaseKeys(data, { deep: true });
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setPendingFlag('transitioningMap', id, false);
      }
    },

    async update(id, payload) {
      try {
        const { data } = await DocumentsAPI.update(id, payload);
        this.updateDocumentInStore(data);
        return camelcaseKeys(data, { deep: true });
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async createVersion(id, formData) {
      this.setPendingFlag('uploadingMap', id, true);
      try {
        const { data } = await DocumentsAPI.createVersion(id, formData);
        this.updateDocumentInStore(data);
        return camelcaseKeys(data, { deep: true });
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setPendingFlag('uploadingMap', id, false);
      }
    },

    async download(id, { versionId = null, filename = 'document' } = {}) {
      this.setPendingFlag('downloadingMap', id, true);
      try {
        const { data } = await DocumentsAPI.download(id, versionId);
        const url = window.URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.setTimeout(() => window.URL.revokeObjectURL(url), 100);
        return true;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setPendingFlag('downloadingMap', id, false);
      }
    },

    async delete(id) {
      this.setUIFlag({ deleting: true });
      try {
        await DocumentsAPI.delete(id);
        this.records = this.records.filter(record => record.id !== id);
        if (this.selectedDocument?.id === id) this.selectedDocument = null;
        return true;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ deleting: false });
      }
    },

    async fetchConversationMessages(id) {
      try {
        const { data } = await DocumentsAPI.getConversationMessages(id);
        this.conversationMessages = camelcaseKeys(data.payload || [], { deep: true });
        return this.conversationMessages;
      } catch (error) {
        throwErrorMessage(error);
        return [];
      }
    },
  },
});
